import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const AdminEnrollments = () => {
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sourceClass, setSourceClass] = useState('');
  const [sourceYear, setSourceYear] = useState('');
  const [targetYear, setTargetYear] = useState('');
  const [decisions, setDecisions] = useState({});
  const [destinations, setDestinations] = useState({});
  const [selected, setSelected] = useState({});

  useEffect(() => {
    fetchClasses();
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    if (targetYear) fetchAvailability();
  }, [targetYear]);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/classes', { headers: { Authorization: `Bearer ${token}` } });
      setClasses(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
  };

  const fetchAcademicYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/admin/academic-years', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const years = Array.isArray(res.data) ? res.data : [];
      setAcademicYears(years);

      const currentYear = years.find((y) => y.is_current && !y.is_closed);
      if (currentYear) {
        setSourceYear(currentYear.year);
        const next = years
          .filter((y) => y.year > currentYear.year && !y.is_closed)
          .sort((a, b) => a.year.localeCompare(b.year))[0];
        if (next) setTargetYear(next.year);
      }
    } catch (err) {
      console.error('Erreur années scolaires:', err);
    }
  };

  const fetchAvailability = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/admin/classes-availability?academic_year=${targetYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailability(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error('Erreur capacité:', err); }
  };

  const loadStudents = async () => {
    if (!sourceClass || !sourceYear) { alert('Sélectionnez la classe source'); return; }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get(`/admin/enrollments?class_id=${sourceClass}&academic_year=${sourceYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(res.data || []);

      const initDec = {}, initDest = {}, initSel = {};
      (res.data || []).forEach((s) => {
        initDec[s.student_id] = 'admis';
        initDest[s.student_id] = '';
        initSel[s.student_id] = true;
      });
      setDecisions(initDec);
      setDestinations(initDest);
      setSelected(initSel);
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
      setStudents([]);
    } finally { setLoading(false); }
  };

  const toggleSelect = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const toggleAll = () => {
    const allSel = students.every((s) => selected[s.student_id]);
    const next = {};
    students.forEach((s) => { next[s.student_id] = !allSel; });
    setSelected(next);
  };

  const setDecision = (studentId, decision) => {
    setDecisions((p) => ({ ...p, [studentId]: decision }));
    if (decision === 'sortant' || decision === 'a_examiner' || decision === 'transfere') {
      setDestinations((p) => ({ ...p, [studentId]: '' }));
    }
  };

  const setDestination = (studentId, classId) => {
    setDestinations((p) => ({ ...p, [studentId]: classId }));
  };

  const handleAutoAssign = () => {
    const targetLevel = window.prompt(
      "🎯 Affectation automatique\n\nEntrez le niveau cible (ex: 5ème, 4ème, 3ème) :",
      ""
    );
    if (!targetLevel) return;

    const availableClasses = availability.filter((c) =>
      c.level && c.level.toLowerCase().includes(targetLevel.toLowerCase()) && !c.is_full
    );

    if (availableClasses.length === 0) {
      alert(`Aucune classe du niveau "${targetLevel}" n'a de place disponible pour ${targetYear}.`);
      return;
    }

    const admis = students.filter((s) => selected[s.student_id] && decisions[s.student_id] === 'admis');
    let classIndex = 0;
    let countInCurrentClass = 0;
    const newDestinations = { ...destinations };

    for (const student of admis) {
      while (
        classIndex < availableClasses.length &&
        countInCurrentClass >= availableClasses[classIndex].available
      ) {
        classIndex++;
        countInCurrentClass = 0;
      }

      if (classIndex >= availableClasses.length) {
        alert(`⚠️ Plus assez de places dans les classes de ${targetLevel} pour tous les admis.`);
        break;
      }

      newDestinations[student.student_id] = availableClasses[classIndex].id;
      countInCurrentClass++;
    }

    setDestinations(newDestinations);
    alert(`✅ Affectation automatique effectuée pour ${admis.length} élève(s) admis dans le niveau ${targetLevel}.`);
  };

  const handlePromote = async () => {
    const list = students.filter((s) => selected[s.student_id]);
    if (list.length === 0) { alert('Aucun élève sélectionné'); return; }

    const missingDest = list.find(
      (s) => (decisions[s.student_id] === 'admis' || decisions[s.student_id] === 'redouble') &&
             !destinations[s.student_id]
    );
    if (missingDest) {
      alert(`⚠️ L'élève "${missingDest.student_name}" n'a pas de classe de destination.`);
      return;
    }

    if (!window.confirm(`Valider le passage pour ${list.length} élève(s) ?`)) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.post('/admin/enrollments/promote', {
        source_class_id: sourceClass,
        source_year: sourceYear,
        target_year: targetYear,
        students: list.map((s) => ({
          student_id: s.student_id,
          decision: decisions[s.student_id] || 'admis',
          target_class_id: destinations[s.student_id] || null,
        })),
      }, { headers: { Authorization: `Bearer ${token}` } });

      alert('✅ ' + res.data.message);
      loadStudents();
      fetchAvailability();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  };

  const stats = () => {
    const list = students.filter((s) => selected[s.student_id]);
    return {
      admis: list.filter((s) => decisions[s.student_id] === 'admis').length,
      redouble: list.filter((s) => decisions[s.student_id] === 'redouble').length,
      a_examiner: list.filter((s) => decisions[s.student_id] === 'a_examiner').length,
      transfere: list.filter((s) => decisions[s.student_id] === 'transfere').length,
      sortant: list.filter((s) => decisions[s.student_id] === 'sortant').length,
      total: list.length,
    };
  };
  const { admis, redouble, a_examiner, transfere, sortant, total } = stats();

  const sourceYearObj = academicYears.find((y) => y.year === sourceYear);

  return (
    <div className="p-6 lg:p-8 bg-white min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#0A0A0A]">🎓 Passage & Orientation</h1>
        <p className="text-gray-500 mt-1">
          Gérez les passages, redoublements et sorties en conservant l'historique
        </p>
      </div>

      {/* SÉLECTIONS */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-xl p-4">
            <h3 className="font-bold text-blue-800 mb-3 text-sm">📤 Année actuelle</h3>
            <select
              value={sourceYear}
              onChange={(e) => setSourceYear(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg mb-2"
            >
              <option value="">-- Choisir --</option>
              {academicYears
                .filter((y) => !y.is_closed)
                .sort((a, b) => b.year.localeCompare(a.year))
                .map((y) => (
                  <option key={y.id} value={y.year}>
                    {y.year} {y.is_current ? '✅ (en cours)' : ''}
                  </option>
                ))}
            </select>
            <select
              value={sourceClass}
              onChange={(e) => setSourceClass(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">-- Classe source --</option>
              {classes.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>

          <div className="bg-green-50 rounded-xl p-4">
            <h3 className="font-bold text-green-800 mb-3 text-sm">📥 Année suivante</h3>
            <select
              value={targetYear}
              onChange={(e) => setTargetYear(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">-- Choisir --</option>
              {academicYears
                .filter((y) => !y.is_closed && y.year > sourceYear)
                .sort((a, b) => a.year.localeCompare(b.year))
                .map((y) => (
                  <option key={y.id} value={y.year}>
                    {y.year}
                  </option>
                ))}
            </select>
            {sourceYear && targetYear && (
              <p className="text-xs text-green-700 mt-2">
                Passage de {sourceYear} → {targetYear}
              </p>
            )}
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={loadStudents}
              disabled={loading || !sourceClass || !sourceYear}
              className="flex-1 bg-[#F39C12] text-white py-3 rounded-lg hover:bg-[#E67E22] font-medium disabled:opacity-50"
            >
              {loading ? '⏳...' : '📋 Charger les élèves'}
            </button>
            {students.length > 0 && (
              <button
                onClick={handleAutoAssign}
                className="bg-purple-500 text-white py-3 px-4 rounded-lg hover:bg-purple-600 font-medium"
                title="Affectation automatique"
              >
                🎯
              </button>
            )}
          </div>
        </div>
      </div>

      {/* STATS */}
      {students.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs text-gray-500">👥 Total</p>
            <p className="text-xl font-bold">{total}</p>
          </div>
          <div className="bg-white rounded-xl border border-green-100 p-4">
            <p className="text-xs text-gray-500">✅ Admis</p>
            <p className="text-xl font-bold text-green-600">{admis}</p>
          </div>
          <div className="bg-white rounded-xl border border-orange-100 p-4">
            <p className="text-xs text-gray-500">🔄 Redouble</p>
            <p className="text-xl font-bold text-orange-600">{redouble}</p>
          </div>
          <div className="bg-white rounded-xl border border-yellow-100 p-4">
            <p className="text-xs text-gray-500">🟠 À examiner</p>
            <p className="text-xl font-bold text-yellow-600">{a_examiner}</p>
          </div>
          <div className="bg-white rounded-xl border border-blue-100 p-4">
            <p className="text-xs text-gray-500">🔵 Transféré</p>
            <p className="text-xl font-bold text-blue-600">{transfere}</p>
          </div>
          <div className="bg-white rounded-xl border border-red-100 p-4">
            <p className="text-xs text-gray-500">⚪ Sortant</p>
            <p className="text-xl font-bold text-red-600">{sortant}</p>
          </div>
        </div>
      )}

      {/* TABLEAU */}
      {students.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-3">
                    <input type="checkbox" checked={students.every((s) => selected[s.student_id])} onChange={toggleAll} className="w-4 h-4" />
                  </th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Matricule</th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Nom</th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Naissance</th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Sexe</th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Parent</th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Décision</th>
                  <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600">Destination</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const decision = decisions[s.student_id] || 'admis';
                  const needsDestination = decision === 'admis' || decision === 'redouble';
                  return (
                    <tr key={s.student_id} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-3">
                        <input type="checkbox" checked={!!selected[s.student_id]} onChange={() => toggleSelect(s.student_id)} className="w-4 h-4" />
                      </td>
                      <td className="py-3 px-3 text-xs font-mono">{s.matricule}</td>
                      <td className="py-3 px-3 text-sm font-medium">
                        {s.student_name}
                        {s.student_email && (<div className="text-[10px] text-gray-400">{s.student_email}</div>)}
                      </td>
                      <td className="py-3 px-3 text-xs">{s.birth_date ? new Date(s.birth_date).toLocaleDateString('fr-FR') : '-'}</td>
                      <td className="py-3 px-3 text-xs">{s.sexe === 'M' ? '👨 M' : s.sexe === 'F' ? '👩 F' : '-'}</td>
                      <td className="py-3 px-3 text-xs">
                        {s.parent_name || '-'}
                        {s.parent_phone && (<div className="text-[10px] text-gray-400">📞 {s.parent_phone}</div>)}
                      </td>
                      <td className="py-3 px-3">
                        <select value={decision} onChange={(e) => setDecision(s.student_id, e.target.value)} className="px-2 py-1 border rounded-lg text-xs">
                          <option value="admis">✅ Admis</option>
                          <option value="redouble">🔄 Redouble</option>
                          <option value="a_examiner">🟠 À examiner</option>
                          <option value="transfere">🔵 Transféré</option>
                          <option value="sortant">⚪ Sortant</option>
                        </select>
                      </td>
                      <td className="py-3 px-3">
                        {needsDestination ? (
                          <select value={destinations[s.student_id] || ''} onChange={(e) => setDestination(s.student_id, e.target.value)} className="px-2 py-1 border rounded-lg text-xs w-full min-w-[140px]">
                            <option value="">-- Choisir --</option>
                            {availability.map((c) => (
                              <option key={c.id} value={c.id} disabled={c.is_full && String(destinations[s.student_id]) !== String(c.id)}>
                                {c.name} ({c.current_count}/{c.max_students}) {c.is_full ? '⚠️ PLEIN' : ''}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 bg-gray-50 flex justify-between items-center flex-wrap gap-3">
            <div className="text-sm text-gray-500">
              💡 Astuce : utilisez 🎯 pour affecter automatiquement les admis
            </div>
            <button onClick={handlePromote} disabled={loading || total === 0} className="px-6 py-3 bg-[#F39C12] text-white rounded-xl hover:bg-[#E67E22] font-bold disabled:opacity-50">
              {loading ? 'Traitement...' : `✅ Valider les passages (${total})`}
            </button>
          </div>
        </div>
      )}

      {students.length === 0 && !loading && sourceClass && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <p className="text-5xl mb-3">📭</p>
          <p className="text-lg font-bold">Aucun élève dans cette classe</p>
          <p className="text-sm text-gray-500 mt-2">Vérifiez l'année et la classe source.</p>
        </div>
      )}

      {students.length === 0 && !sourceClass && (
        <div className="bg-blue-50 rounded-2xl border border-blue-100 p-8 text-center">
          <p className="text-5xl mb-3">🎓</p>
          <p className="text-lg font-bold mb-2">Sélectionnez une classe source</p>
          <p className="text-sm text-gray-500">Choisissez l'année et la classe pour charger les élèves.</p>
        </div>
      )}
    </div>
  );
};

export default AdminEnrollments;